import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, MessageSquare, Mail, Users, 
  MapPin, Star, Send, Building,
  ArrowRight, AlertCircle, Clock,
  Facebook, Instagram, Smartphone, Check,
  ExternalLink, Calendar, Shield, Flag,
  LogIn, Sparkles, Award, Verified
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/toast/use-toast';
import ProfileImage from './ProfileImage';
import ReportForm from './ReportForm';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardAnimation = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { 
      type: "spring", 
      damping: 15,
      duration: 0.6
    }
  },
  hover: {
    y: -8,
    scale: 1.02,
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
    transition: { duration: 0.3 }
  }
};

const ContactSection = ({ 
  id = 'contacto', 
  property, 
  handleContact,
  isRental = false
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [focusedInput, setFocusedInput] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: useMemo(() => `Estoy interesado en ${isRental ? 'el alquiler' : 'la propiedad'}: ${property?.titulo || ''}`, 
                    [isRental, property?.titulo])
  });

  // Memoize vendor data
  const vendedor = useMemo(() => {
    return property?.vendedor || {
      nombre: 'KAIROS INMOBILIARIA',
      foto_perfil: null,
      tipo_usuario_id: 1,
      email_verificado: false,
      telefono_verificado: false,
      ubicacion: 'PIURA, PERÚ',
      sobre_mi: '',
      sobre_nosotros: '',
      whatsapp: '',
      telefono: '',
      facebook: '',
      instagram: '',
      nombre_usuario: ''
    };
  }, [property?.vendedor]);

  // Memorize stable particle properties to prevent re-renders
  const particles = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => ({
      id: i,
      size: Math.random() * 80 + 40,
      x: Math.random() * 100,
      y: Math.random() * 100,
      duration: Math.random() * 20 + 20,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.1 + 0.05
    }));
  }, []); // Empty dependency array ensures this only runs once

  // Memoize helper functions
  const redirectToLogin = useCallback(() => {
    toast({
      title: "Debes iniciar sesión",
      description: "Para contactar al vendedor necesitas tener una cuenta",
      variant: "destructive",
    });
    localStorage.setItem('redirectAfterLogin', window.location.pathname);
    navigate('/login');
  }, [navigate, toast]);

  const handleProfileClick = useCallback(() => {
    if (!vendedor.nombre_usuario || !vendedor.tipo_usuario_id) {
      console.warn('Datos de perfil incompletos:', vendedor);
      return;
    }

    const profilePath = getProfileRoute(vendedor.tipo_usuario_id, vendedor.nombre_usuario);
    if (profilePath) {
      navigate(profilePath);
    } else {
      console.warn('No se pudo determinar la ruta del perfil');
    }
  }, [vendedor, navigate]);

  const handleWhatsAppContact = useCallback(() => {
    if (!user) {
      redirectToLogin();
      return;
    }

    if (!vendedor.whatsapp) {
      toast({
        title: "WhatsApp no disponible",
        description: "El vendedor no ha proporcionado un número de WhatsApp",
        variant: "destructive",
      });
      return;
    }

    const message = `Estoy interesado en ${isRental ? 'el alquiler' : 'la propiedad'}: ${property.titulo}`;
    const whatsappUrl = `https://wa.me/${vendedor.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  }, [user, vendedor.whatsapp, isRental, property?.titulo, toast, redirectToLogin]);

  const handlePhoneContact = useCallback(() => {
    if (!user) {
      redirectToLogin();
      return;
    }

    if (!vendedor.telefono) {
      toast({
        title: "Teléfono no disponible",
        description: "El vendedor no ha proporcionado un número de teléfono",
        variant: "destructive",
      });
      return;
    }

    const prefijo = vendedor.prefijo_telefono || '+51';
    window.location.href = `tel:${prefijo}${vendedor.telefono}`;
  }, [user, vendedor.telefono, vendedor.prefijo_telefono, toast, redirectToLogin]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!user) {
      redirectToLogin();
      return;
    }
    
    if (loading) return;

    if (!formData.name || !formData.email || !formData.phone) {
      toast({
        title: "Campos requeridos",
        description: "Por favor, completa todos los campos obligatorios",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const contactPayload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        propertyId: property?.id,
        userId: user?.id
      };

      const response = await fetch('https://backend-production-1786.up.railway.app/api/contactos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(contactPayload)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.error || 
          responseData.message || 
          'Error al enviar el mensaje'
        );
      }

      setFormSubmitted(true);
      toast({
        title: "Mensaje enviado",
        description: "Nos pondremos en contacto contigo pronto",
        variant: "success",
      });

      setTimeout(() => {
        setFormSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: `Estoy interesado en ${isRental ? 'el alquiler' : 'la propiedad'}: ${property?.titulo || ''}`
        });
      }, 3000);

    } catch (error) {
      toast({
        title: "Error al enviar mensaje",
        description: error.message || "No se pudo enviar el mensaje. Por favor, intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [formData, loading, property?.id, property?.titulo, user, isRental, toast, redirectToLogin]);

  const handleReportClick = useCallback(() => {
    if (!user) {
      toast({
        title: "Debes iniciar sesión",
        description: "Para reportar una propiedad necesitas tener una cuenta",
        variant: "destructive",
      });
      return;
    }
    setReportModalOpen(true);
  }, [user, toast]);

  // Utility functions
  const getTipoUsuario = useCallback((tipoId) => {
    const tipos = {
      1: 'Cliente',
      2: 'Vendedor',
      3: 'Constructora',
      4: 'Inmobiliaria'
    };
    return tipos[tipoId] || 'Vendedor';
  }, []);

  const getProfileRoute = useCallback((tipoId, nombre_usuario) => {
    if (!tipoId || !nombre_usuario) return null;

    const typeMapping = {
      2: 'vendedor',
      3: 'constructora',
      4: 'inmobiliaria'
    };

    const type = typeMapping[tipoId];
    return type ? `/${type}/${nombre_usuario}` : null;
  }, []);

  // Color scheme based on vendor type
  const colors = useMemo(() => {
    const tipoId = vendedor.tipo_usuario_id;
    
    switch(tipoId) {
      case 2: // Vendedor - azul
        return {
          primary: 'from-blue-600 via-blue-500 to-purple-600',
          secondary: 'bg-blue-600',
          text: 'text-blue-600 dark:text-blue-400',
          hover: 'hover:bg-blue-700',
          button: 'from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700',
          ring: 'ring-blue-500 ring-blue-200',
          border: 'border-blue-500 dark:border-blue-400',
          gradientText: 'from-blue-500 to-purple-600',
          hoverBg: 'hover:bg-blue-50 dark:hover:bg-blue-900/20',
          light: 'bg-blue-50 dark:bg-blue-900/20',
          accent: 'text-blue-500 dark:text-blue-400',
          glowColor: 'blue'
        };
      case 3: // Constructora - dorado
        return {
          primary: 'from-amber-500 via-orange-500 to-red-500',
          secondary: 'bg-amber-600',
          text: 'text-amber-600 dark:text-amber-400',
          hover: 'hover:bg-amber-700',
          button: 'from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700',
          ring: 'ring-amber-500 ring-amber-200',
          border: 'border-amber-500 dark:border-amber-400',
          gradientText: 'from-amber-500 to-orange-600',
          hoverBg: 'hover:bg-amber-50 dark:hover:bg-amber-900/20',
          light: 'bg-amber-50 dark:bg-amber-900/20',
          accent: 'text-amber-500 dark:text-amber-400',
          glowColor: 'amber'
        };
      case 4: // Inmobiliaria - plateado
        return {
          primary: 'from-slate-600 via-gray-500 to-zinc-600',
          secondary: 'bg-gray-600',
          text: 'text-gray-600 dark:text-gray-400',
          hover: 'hover:bg-gray-700',
          button: 'from-slate-600 to-zinc-600 hover:from-slate-700 hover:to-zinc-700',
          ring: 'ring-gray-500 ring-gray-200',
          border: 'border-gray-500 dark:border-gray-400',
          gradientText: 'from-slate-500 to-zinc-600',
          hoverBg: 'hover:bg-gray-50 dark:hover:bg-gray-900/20',
          light: 'bg-gray-50 dark:bg-gray-900/20',
          accent: 'text-gray-500 dark:text-gray-400',
          glowColor: 'gray'
        };
      default:
        return {
          primary: 'from-blue-600 via-blue-500 to-purple-600',
          secondary: 'bg-blue-600',
          text: 'text-blue-600 dark:text-blue-400',
          hover: 'hover:bg-blue-700',
          button: 'from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700',
          ring: 'ring-blue-500 ring-blue-200',
          border: 'border-blue-500 dark:border-blue-400',
          gradientText: 'from-blue-500 to-purple-600',
          hoverBg: 'hover:bg-blue-50 dark:hover:bg-blue-900/20',
          light: 'bg-blue-50 dark:bg-blue-900/20',
          accent: 'text-blue-500 dark:text-blue-400',
          glowColor: 'blue'
        };
    }
  }, [vendedor.tipo_usuario_id]);

  // Contact Button Component
  const ContactButton = useCallback(({ icon: Icon, children, variant = 'default', onClick, type = 'button', disabled, requiresLogin = true }) => {
    let buttonColors;
    
    if (variant === 'whatsapp') {
      buttonColors = "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg shadow-green-500/25";
    } else if (variant === 'call') {
      buttonColors = "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-700 dark:text-gray-200 shadow-lg dark:shadow-black/30";
    } else if (variant === 'login') {
      buttonColors = "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg shadow-blue-500/25";
    } else {
      buttonColors = `bg-gradient-to-r ${colors.button} text-white shadow-lg shadow-${colors.glowColor}-500/25`;
    }
    
    if (requiresLogin && !user) {
      return (
        <motion.button
          onClick={redirectToLogin}
          type="button"
          className="group relative overflow-hidden w-full py-4 px-6 rounded-2xl
                    flex items-center justify-center gap-3 font-semibold
                    transition-all duration-300 ease-out
                    bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white 
                    shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                          translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
          <LogIn className="w-5 h-5 relative z-10" />
          <span className="relative z-10">Iniciar sesión</span>
        </motion.button>
      );
    }
    
    return (
      <motion.button
        onClick={onClick}
        type={type}
        disabled={disabled}
        className={`group relative overflow-hidden w-full py-4 px-6 rounded-2xl
                  flex items-center justify-center gap-3 font-semibold
                  transition-all duration-300 ease-out
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  ${buttonColors}`}
        whileHover={{ scale: disabled ? 1 : 1.02, y: disabled ? 0 : -2 }}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                        translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
        {Icon && <Icon className="w-5 h-5 relative z-10" />}
        <span className="relative z-10">{children}</span>
      </motion.button>
    );
  }, [colors.button, colors.glowColor, user, redirectToLogin]);

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      id={id} 
      className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Profile Section */}
        <motion.div 
          variants={cardAnimation}
          whileHover="hover"
          className="h-full backdrop-blur-xl bg-white/80 dark:bg-white/10 rounded-3xl shadow-2xl dark:shadow-black/50 overflow-hidden border border-white/50 dark:border-white/10"
        >
          {/* Enhanced Header Banner */}
          <div className={`relative h-40 bg-gradient-to-r ${colors.primary} overflow-hidden`}>
            {/* Stable Animated Particles */}
            {particles.map((particle) => (
              <motion.div
                key={particle.id}
                className="absolute rounded-full bg-white/10 blur-xl"
                style={{
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  opacity: particle.opacity,
                }}
                animate={{
                  x: [0, 30, -20, 0],
                  y: [0, -20, 30, 0],
                  scale: [1, 1.2, 0.8, 1],
                }}
                transition={{
                  duration: particle.duration,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: particle.delay,
                }}
              />
            ))}
            
            {/* Geometric Pattern Overlay */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0" 
                   style={{
                     backgroundImage: `radial-gradient(circle at 20px 20px, white 2px, transparent 2px)`,
                     backgroundSize: '40px 40px'
                   }} />
            </div>

            {/* Floating Icons */}
            <motion.div
              className="absolute top-4 right-4"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles className="w-6 h-6 text-white/30" />
            </motion.div>
          </div>
          
          {/* Enhanced Profile Content */}
          <div className="relative px-8 pt-0 pb-8">
            <div className="flex flex-col items-center">
              {/* Enhanced Profile Picture */}
              <div 
                onClick={handleProfileClick} 
                className="relative group cursor-pointer mx-auto -mt-20"
              >
                <div className="absolute -inset-2 bg-gradient-to-r from-white via-white/80 to-white/60 dark:from-gray-800 dark:via-gray-700/80 dark:to-gray-600/60
                              rounded-full blur-lg opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative rounded-full p-1 bg-white dark:bg-gray-800 shadow-2xl dark:shadow-black/50">
                  <ProfileImage
                    url={vendedor.foto_perfil}
                    alt={vendedor.nombre || "Perfil"}
                    className="w-32 h-32 rounded-full object-cover
                            transform transition-all duration-500 
                            group-hover:scale-105"
                  />
                  
                  {/* Status Indicator */}
                  {(vendedor.email_verificado && vendedor.telefono_verificado) && (
                    <div className="absolute -bottom-2 -right-2 bg-green-500 text-white p-2 rounded-full shadow-lg">
                      <Verified className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Name and Info */}
              <div className="mt-6 text-center space-y-6">
                <div>
                  <h2 
                    onClick={handleProfileClick}
                    className={`text-3xl font-bold bg-gradient-to-r bg-clip-text dark:!text-white dark:bg-none ${colors.gradientText} cursor-pointer
                            transform transition-all duration-300 hover:scale-105 mb-2`}
                  >
                    {vendedor.nombre || "KAIROS INMOBILIARIA"}
                  </h2>
                  
                  <div className="flex flex-wrap justify-center gap-3">
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm ${colors.light} ${colors.text} font-semibold`}>
                      <Users className="w-4 h-4" />
                      {getTipoUsuario(vendedor.tipo_usuario_id)}
                    </span>

                    {(vendedor.email_verificado && vendedor.telefono_verificado) && (
                      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm 
                                  bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-semibold">
                        <Shield className="w-4 h-4" />
                        Verificado
                      </span>
                    )}

                    {vendedor.ubicacion && (
                      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm 
                                  bg-gray-100 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 font-semibold">
                        <MapPin className="w-4 h-4" />
                        {vendedor.ubicacion}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Description */}
            <div className="mt-8 p-6 bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-800/50 dark:to-gray-700/50 rounded-2xl backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50">
              <h3 className={`text-xl font-bold ${colors.text} mb-4 flex items-center gap-2`}>
                <Award className="w-5 h-5" />
                {vendedor.tipo_usuario_id === 2 ? 'Sobre Mí' : 'Sobre Nosotros'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {vendedor.tipo_usuario_id === 2
                  ? vendedor.sobre_mi || "Vendedor profesional con experiencia en el mercado inmobiliario. Comprometido con brindar el mejor servicio a mis clientes."
                  : vendedor.sobre_nosotros || "Somos una empresa dedicada al sector inmobiliario con amplia experiencia en el mercado peruano. Nuestro compromiso es brindar un servicio de calidad para satisfacer las necesidades de nuestros clientes."}
              </p>
            </div>

            {/* Enhanced Action Buttons */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              <ContactButton 
                icon={Smartphone}
                variant="whatsapp"
                onClick={handleWhatsAppContact}
                requiresLogin={true}
              >
                WhatsApp
              </ContactButton>
              <ContactButton 
                icon={Phone}
                variant="call"
                onClick={handlePhoneContact}
                requiresLogin={true}
              >
                Llamar
              </ContactButton>
            </div>
            
            {/* Report Button */}
            <div className="mt-6">
              <motion.button
                onClick={handleReportClick}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 
                           text-red-600 dark:text-red-400 bg-red-50/80 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30
                           border border-red-200 dark:border-red-800/50 rounded-2xl backdrop-blur-sm
                           transition-all duration-300 text-sm font-semibold"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Flag className="w-4 h-4" />
                Reportar esta publicación
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Contact Form */}
        <motion.div 
          variants={cardAnimation}
          whileHover="hover"
          className="backdrop-blur-xl bg-white/80 dark:bg-white/10 rounded-3xl shadow-2xl dark:shadow-black/50 p-10 relative overflow-hidden border border-white/50 dark:border-white/10"
        >
          <AnimatePresence>
            {formSubmitted ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="absolute inset-0 bg-gradient-to-br from-green-500 to-green-600 z-20 flex flex-col items-center justify-center text-white rounded-3xl"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: [0, 1.3, 1] }}
                  transition={{ duration: 0.6, times: [0, 0.7, 1] }}
                  className="bg-white/20 rounded-full p-6 mb-6 backdrop-blur-sm"
                >
                  <Check className="w-20 h-20" />
                </motion.div>
                <h3 className="text-3xl font-bold mb-3">¡Mensaje Enviado!</h3>
                <p className="text-center px-4 text-white/90 text-lg">Nos pondremos en contacto contigo pronto.</p>
              </motion.div>
            ) : null}
          </AnimatePresence>

          {/* Form Header */}
          <div className="mb-10">
            <h2 className={`text-3xl font-bold flex items-center gap-4 bg-gradient-to-r ${colors.gradientText} bg-clip-text dark:!text-white dark:bg-none`}>
              <MessageSquare className={`${colors.accent} w-8 h-8`} />
              Contáctanos
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mt-3 flex items-center gap-3 text-lg">
              <Clock className={`w-6 h-6 ${colors.accent}`} />
              Responderemos en menos de 24 horas
            </p>
          </div>

          {/* Login Required Message */}
          {!user && (
            <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/20 rounded-2xl border border-blue-200 dark:border-blue-800/50 backdrop-blur-sm">
              <h3 className="text-xl font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-3 mb-3">
                <LogIn className="w-6 h-6" />
                Inicia sesión para contactar
              </h3>
              <p className="text-blue-600 dark:text-blue-400 mb-6">
                Para enviar un mensaje al vendedor, debes iniciar sesión o crear una cuenta.
              </p>
              <ContactButton
                icon={LogIn}
                variant="login"
                onClick={redirectToLogin}
                requiresLogin={false}
              >
                Iniciar Sesión
              </ContactButton>
            </div>
          )}

          {/* Enhanced Form */}
          {user && (
            <form onSubmit={handleSubmit} className="space-y-8">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-3"
              >
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Nombre Completo *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-6 py-4 rounded-2xl border-2 bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm transition-all duration-300 
                      text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500
                      ${formData.name ? colors.border : 'border-gray-200 dark:border-gray-600'} 
                      focus:outline-none focus:ring-4 focus:ring-${colors.glowColor}-500/20 focus:border-${colors.glowColor}-500 dark:focus:border-${colors.glowColor}-400`}
                    placeholder="Tu nombre completo"
                    required
                    onFocus={() => setFocusedInput('name')}
                    onBlur={() => setFocusedInput(null)}
                  />
                  <Users className={`absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 
                               ${formData.name ? colors.text : 'text-gray-400 dark:text-gray-500'}`} />
                </div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="space-y-3"
                >
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Correo Electrónico *
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-6 py-4 rounded-2xl border-2 bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm transition-all duration-300 
                        text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500
                        ${formData.email ? colors.border : 'border-gray-200 dark:border-gray-600'} 
                        focus:outline-none focus:ring-4 focus:ring-${colors.glowColor}-500/20 focus:border-${colors.glowColor}-500 dark:focus:border-${colors.glowColor}-400`}
                      placeholder="tu@email.com"
                      required
                      onFocus={() => setFocusedInput('email')}
                      onBlur={() => setFocusedInput(null)}
                    />
                    <Mail className={`absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 
                                 ${formData.email ? colors.text : 'text-gray-400 dark:text-gray-500'}`} />
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  className="space-y-3"
                >
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Teléfono *
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-6 py-4 rounded-2xl border-2 bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm transition-all duration-300 
                        text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500
                        ${formData.phone ? colors.border : 'border-gray-200 dark:border-gray-600'} 
                        focus:outline-none focus:ring-4 focus:ring-${colors.glowColor}-500/20 focus:border-${colors.glowColor}-500 dark:focus:border-${colors.glowColor}-400`}
                      placeholder="Tu número de teléfono"
                      required
                      onFocus={() => setFocusedInput('phone')}
                      onBlur={() => setFocusedInput(null)}
                    />
                    <Phone className={`absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 
                                    ${formData.phone ? colors.text : 'text-gray-400 dark:text-gray-500'}`} />
                  </div>
                </motion.div>
              </div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="space-y-3"
              >
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Mensaje
                </label>
                <div className="relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className={`w-full px-6 py-4 rounded-2xl border-2 bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm transition-all duration-300 
                      text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500
                      ${formData.message ? colors.border : 'border-gray-200 dark:border-gray-600'} 
                      focus:outline-none focus:ring-4 focus:ring-${colors.glowColor}-500/20 focus:border-${colors.glowColor}-500 dark:focus:border-${colors.glowColor}-400`}
                    placeholder="Escribe tu mensaje aquí..."
                  />
                  <MessageSquare className={`absolute right-4 top-6 w-5 h-5 
                                        ${formData.message ? colors.text : 'text-gray-400 dark:text-gray-500'}`} />
                </div>
              </motion.div>

              <ContactButton
                icon={Send}
                type="submit"
                disabled={loading}
                requiresLogin={false}
              >
                {loading ? 'Enviando...' : 'Enviar Mensaje'}
              </ContactButton>
            </form>
          )}
        </motion.div>
      </div>

      {/* Report Modal */}
      <AnimatePresence>
        {reportModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-4"
          >
            <div className="w-full max-w-md">
              <ReportForm 
                property={property} 
                colors={colors} 
                onClose={() => setReportModalOpen(false)} 
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default React.memo(ContactSection);